import { memo, useEffect, useMemo, useRef } from 'react';
import type { StreamMessageEvents, ToolStepPart } from '@/entities/ai';
import { getChatStores } from '@/features/chats/lib/chat-store-bindings';
import { useToolStepTitle, type ToolStepTitle } from '@/features/chats/model/toolStepTitle';
import { useChatStore } from '@/features/chats/stores';
import { AgentTaskRecord, AgentTaskStatus } from '../AgentTask';
import { AgentThinkingProcess } from '../AgentThinkingProcess';
import { AgentResponse } from '../ChatBubble/AgentResponse/AgentResponse';
import { ChatBubble } from '../ChatBubble/ChatBubble';
import { ChatMessageProps } from './ChatMessage.types';
import { MessageAttachments } from './MessageAttachments';

/** Gives each tool step the title its conversation names it by, where it names one. */
function retitle<T extends StreamMessageEvents>(
  events: T[],
  stepTitle: ToolStepTitle | undefined,
  partsByCallId: Record<string, ToolStepPart[]> | undefined,
): T[] {
  if (!stepTitle || !partsByCallId) return events;
  return events.map((event) => {
    if (
      event.type !== 'data-toolStart' &&
      event.type !== 'data-toolComplete' &&
      event.type !== 'data-toolError'
    ) {
      return event;
    }
    const { callId } = event.data;
    if (!(callId in partsByCallId)) return event;
    const toolName = stepTitle({ callId, parts: partsByCallId[callId] });
    return toolName ? ({ ...event, data: { ...event.data, toolName } } as T) : event;
  });
}

export const ChatMessage = memo<ChatMessageProps>(
  ({ msg, typewriter, isLastMessage, agentId }) => {
    const { events } = useChatStore((state) => ({
      events: state.events,
    }));
    const stepTitle = useToolStepTitle();
    const messageRef = useRef<HTMLDivElement>(null);
    const { type, content, messageId, files } = msg;
    // Subscribe to widgetMessages for this specific messageId so component re-renders when widgets are added
    const widgetStoreHook = getChatStores().widgetStore.useWidgetStore;
    const messageWidgetAgents = widgetStoreHook((state) =>
      messageId ? state.widgetMessages[messageId] : undefined,
    );
    const { hasWidgetForMessage } = widgetStoreHook();

    // Memoize expensive computations
    const hasWidget = useMemo(() => {
      // messageWidgetAgents triggers re-render when widgets change for this message
      void messageWidgetAgents;
      return messageId ? hasWidgetForMessage(messageId) : false;
    }, [messageId, hasWidgetForMessage, messageWidgetAgents]);

    const hasContent = useMemo(() => {
      return Boolean(content);
    }, [content]);

    // A reply that only asked, and was moved on from, has its questions to show and nothing more.
    const askedQuestions = type === 'agent' ? msg.askedQuestions : undefined;
    const shouldShowContent = useMemo(() => {
      return type === 'user' || hasContent || hasWidget || askedQuestions !== undefined;
    }, [type, hasContent, hasWidget, askedQuestions]);

    const shouldShowLoading = useMemo(() => {
      return type === 'agent' && !hasContent && !hasWidget && askedQuestions === undefined;
    }, [type, hasContent, hasWidget, askedQuestions]);

    // Always scroll for the last message (simpler and more reliable)
    const hasScrolled = useRef(false);
    // Extract isStreaming safely - only exists on AgentMessage
    const isStreaming = type === 'agent' && 'isStreaming' in msg ? msg.isStreaming : false;
    useEffect(() => {
      if (isLastMessage) {
        // Ignore autoscroll when streaming is enabled
        if (type === 'agent' && isStreaming && hasScrolled.current) return;
        const timer = setTimeout(() => {
          if (!hasScrolled.current) hasScrolled.current = true;
          messageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }, 100); // Small delay for DOM updates

        return () => clearTimeout(timer);
      }
    }, [isLastMessage, content, isStreaming, type]);

    const toolStepParts = type === 'agent' ? msg.toolStepParts : undefined;
    const toolSteps = type === 'agent' ? msg.toolSteps : undefined;

    // While the reply has nothing to show, the chat's live events say which step is running.
    const AgentTaskComponent = useMemo(() => {
      if (events.size > 0) {
        return (
          <AgentTaskStatus
            events={retitle(Array.from(events.values()), stepTitle, toolStepParts)}
          />
        );
      }

      return (
        <div className="pl-4 mt-8 mb-2">
          <div className="dot-typing" data-testid="loading-dots"></div>
        </div>
      );
    }, [events, stepTitle, toolStepParts]);

    // Once it does, the steps it took stay above it, read from the message itself.
    const stepRecord = useMemo(
      () =>
        toolSteps?.length ? (
          <AgentTaskRecord
            steps={retitle(toolSteps, stepTitle, toolStepParts)}
            settled={!isStreaming}
          />
        ) : undefined,
      [toolSteps, stepTitle, toolStepParts, isStreaming],
    );

    const autosentMessageToHide = msg.metadata?.isHidden;
    if (autosentMessageToHide) {
      return <div className="h-10" data-testid="autosent-message" />;
    }

    return (
      <div
        ref={messageRef}
        key={messageId}
        data-testid="chat-message"
        className={isLastMessage ? 'min-h-80' : undefined}
      >
        {/* TODO: Remove hidden prop when designers have a "vision" */}
        {type === 'agent' && (
          <AgentThinkingProcess
            thinkingProcesses={msg.thinkingProcesses ?? []}
            isThinking={msg.isStreaming ?? false}
            hidden
          />
        )}
        {shouldShowContent && (
          <ChatBubble
            content={typeof content === 'object' ? content.text : content || ''}
            files={files ?? []}
            type={type}
            typewriter={hasWidget ? false : typewriter} // Disable typewriter for widget messages for stability
            messageId={messageId}
            hasWidget={hasWidget}
            isStreaming={isStreaming}
            metadata={msg.metadata}
            steps={stepRecord}
            askedQuestions={askedQuestions}
            {...(type === 'agent' ? { agentId } : {})}
          />
        )}

        {shouldShowLoading && msg.type === 'agent' && (
          <AgentResponse agentId={agentId}>
            <MessageAttachments files={msg.files} type="assistant" agentId={agentId} />
            {AgentTaskComponent}
          </AgentResponse>
        )}
      </div>
    );
  },
  // Custom comparison function for better performance
  (prevProps, nextProps) => {
    // Only re-render if the message content, typewriter setting, or last message status changes
    if (nextProps.msg.type === 'agent' && nextProps.msg.isStreaming) {
      return false;
    }

    return (
      prevProps.msg.messageId === nextProps.msg.messageId &&
      prevProps.msg.content === nextProps.msg.content &&
      prevProps.msg.type === nextProps.msg.type &&
      prevProps.msg.files === nextProps.msg.files &&
      prevProps.typewriter === nextProps.typewriter &&
      prevProps.isLastMessage === nextProps.isLastMessage &&
      prevProps.agentId === nextProps.agentId
    );
  },
);

ChatMessage.displayName = 'ChatMessage';
